import { useEffect } from 'react';
import { useStore } from 'jotai';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { ErrorComponent, Navigate, Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { campaignConversionActionsQueryOptions, campaignQueryOptions, campaignTaskTrackingQueryOptions, excludedLocationsQueryOptions } from '@/lib/query-options';
import { HttpError } from '@/lib/apis';
import { PageLoadingState } from '@/components/glitch-ui/empty-state';
import {
  campaignDataAtom,
  metaCampaignDataAtom,
  campaignTaskTrackingAtom,
  isLoadingCampaignAtom,
} from '@/store';
import { useCampaignDraft } from '@/hooks/useCampaignDraft';
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning';
import { useAutoReverseSync } from '@/hooks/useAutoReverseSync';
import { useReverseSyncInvalidation } from '@/hooks/useReverseSyncInvalidation';
import { useAdSyncFailureToast } from '@/hooks/useAdSyncFailureToast';

export const Route = createFileRoute('/_authenticated/_layout/campaigns/google/$campaignslug/_slug')({
  component: CampaignLayout,
  // The loader waits on the campaign: show the page loading state, never a blank page (#6780).
  // PageLoadingState owns the anti-flash delay, so the router shows it straight away and holds it no longer
  // than the load (TanStack's own defaults would leave the page blank for the first second).
  pendingComponent: PageLoadingState,
  pendingMs: 0,
  pendingMinMs: 0,
  // #6477: a 404 while the page is already mounted (e.g. the org changed and the
  // suspense query refetched into the new org's 404) must land the user on their
  // own campaign list, never on an error boundary.
  errorComponent: ({ error }) => {
    if (error instanceof HttpError && error.status === 404) {
      return <Navigate to="/campaigns" replace />;
    }
    return <ErrorComponent error={error} />;
  },
  loader: async ({ params, context }) => {
    const { campaignslug } = params;
    const queryClient = context.queryClient;

    try {
      const campaign = await queryClient.ensureQueryData(campaignQueryOptions(campaignslug));
      return {
        crumb: campaign.name,
      };
    } catch (error) {
      // A genuine 404 covers both deleted campaigns and campaigns belonging to a
      // different organization (#6448). Either way the user gets THEIR campaign
      // list, not an error page (#6477). Everything else (500s, network drops,
      // timeouts) must still surface in the route error boundary (#5001).
      if (error instanceof HttpError && error.status === 404) {
        throw redirect({ to: '/campaigns', replace: true });
      }
      throw error;
    }
  },
});

function CampaignLayout() {
  const { campaignslug } = Route.useParams();
  const store = useStore();

  const { isLoading, data: campaign } = useSuspenseQuery(campaignQueryOptions(campaignslug));

  const { data: campaignTaskTracking } = useQuery(
    campaignTaskTrackingQueryOptions(campaign.id),
  );

  // Staged into the draft so excluded-location edits go through Save / the unsaved-changes guard.
  const { data: excludedLocationsData } = useQuery(excludedLocationsQueryOptions(campaign.id));

  // Same staging pattern for Conversions-tab row edits (#6479): the baseline hydrates here so
  // Save Changes and the unsaved-changes guard cover staged conversion edits on every sub-page.
  const { data: conversionLinksData } = useQuery(campaignConversionActionsQueryOptions(campaign.id));

  // Sync campaign data to atoms for use by child components
  useEffect(() => {
    store.set(campaignDataAtom, campaign);
    // Selecting a Google campaign clears the Meta selection so /campaigns restores this one (#4660).
    store.set(metaCampaignDataAtom, null);
    store.set(campaignTaskTrackingAtom, campaignTaskTracking);
    store.set(isLoadingCampaignAtom, isLoading);
  }, [store, campaign, campaignTaskTracking, isLoading]);

  // Surface a rejected live ad-content sync when it happens (#5598, #6297). Mounted once in the shared
  // layout so the overview and every ad page share one baselined toast: a failure that predates this
  // visit is never replayed on open, which the old mount-scoped effect here did on every visit (#6583).
  // The "!" / Deploy button still marks the unapplied change separately via budget_state.
  useAdSyncFailureToast(campaign.id, campaignTaskTracking?.campaign_ads_ga_sync);

  // Initialize campaign draft state and unsaved changes warning at the layout level
  // This ensures the draft persists across all campaign sub-pages (ads, keywords, etc.)
  const { isDirty, saveChanges, discardChanges, isSaving } = useCampaignDraft(campaign, excludedLocationsData?.locations, conversionLinksData);

  // Auto-pull on open (#5566 Req 1): detect Google Ads drift once per mount (throttled 15 min) and
  // pull it in. Mounted here in the shared layout so it covers the overview and ad sub-pages without
  // re-firing per ad (the ad route remounts via key=, this layout does not).
  useAutoReverseSync(campaign);
  // Durable cache invalidation for reverse syncs (manual, auto-pull, or nightly job). Mounted here
  // so it fires once and survives campaign↔ad navigation, unlike the old per-instance block (#5745).
  useReverseSyncInvalidation(campaign);
  const { UnsavedChangesModal } = useUnsavedChangesWarning({
    isDirty,
    onSave: saveChanges,
    onDiscard: discardChanges,
    isSaving,
    allowedBasePath: `/campaigns/google/${campaignslug}`,
    contextName: campaign.name,
  });

  // Removed automatic redirect to generating page - users can navigate freely
  return (
    <>
      <Outlet />
      {UnsavedChangesModal}
    </>
  );
}
